import React, { forwardRef, useState, ReactNode, isValidElement, useEffect, ChangeEvent, MouseEvent } from 'react'
import { SIZES, RADIO_BUTTON_DISPLAY_NAME, KIND, ICON, RADIO_FOOTER_DISPLAY_NAME, RADIO_DISPLAY_NAME } from './constants'
import styles from './style.module.scss'
import { RadioButton } from './RadioButton'
import { LabelPlacement, LabelWidth, RadioButtonSizes, RadioIcon, RadioKind } from './types'
import { getChildProps } from '../utils/react'
import { Override } from '../Option'
import { warn } from '../utils/console'
import { RadioGroupContext } from './RadioGroupContext'

export interface RadioGroupProps {
  children?: ReactNode
  value?: string
  defaultValue?: string
  disabled?: boolean
  id?: string
  indent?: boolean
  kind?: RadioKind
  icon?: RadioIcon
  name?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onClick?: (e: MouseEvent<HTMLInputElement>) => void
  options?: string[]
  reversed?: boolean
  labelWidth?: LabelWidth
  labelPlacement?: LabelPlacement
  size?: RadioButtonSizes
  tabIndex?: number
  testNameSpace?: string
  'aria-describedby'?: string
  'aria-label'?: string
  'aria-labelledby'?: string
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>((props, ref) => {
  const {
    children,
    name,
    onChange,
    onClick,
    size = SIZES.MEDIUM,
    kind = KIND.DEFAULT,
    icon = ICON.DEFAULT,
    defaultValue = '',
    disabled,
    testNameSpace = '',
    options,
    id = Math.random().toString(36),
    reversed,
    indent,
    labelWidth,
    labelPlacement,
    ...otherProps
  } = props
  const hasValueProp = 'value' in props
  const [selectedValue, setSelectedValue] = useState(props.value ?? defaultValue)

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    !hasValueProp && setSelectedValue(e.target.value)
    onChange?.(e)
  }

  const childMatchesDisplayName = (child: any, displayName: string): boolean => child?.type.displayName === displayName

  const renderChildren = (): ReactNode => {
    const childrenList = React.Children.toArray(children)

    // @ts-expect-error
    const lastRadioButtonIndex = childrenList.map((child) => child.type.displayName).lastIndexOf(RADIO_BUTTON_DISPLAY_NAME)
    const hasFooter = childrenList.some((child) => childMatchesDisplayName(child, RADIO_FOOTER_DISPLAY_NAME))
    const lastChildIsFooter = childMatchesDisplayName(childrenList[childrenList.length - 1], RADIO_FOOTER_DISPLAY_NAME)

    if (hasFooter && !lastChildIsFooter) {
      warn('RadioFooter should be the last child of RadioGroup when using children as options.')
    }

    return childrenList.map((child, index) => {
      if (!isValidElement(child)) {
        return null
      }
      const isRadioButton = childMatchesDisplayName(child, RADIO_BUTTON_DISPLAY_NAME) || childMatchesDisplayName(child, RADIO_DISPLAY_NAME)

      if (!isRadioButton) {
        return child
      }

      const childProps = getChildProps(child)
      const childId = `${id}-${index}`
      const shouldApplyBorderOverrides = index === lastRadioButtonIndex && lastChildIsFooter
      return (
        <RadioButton
          key={childId}
          id={childId}
          {...(shouldApplyBorderOverrides && { optionOverride: [Override.DisabledBottomBorder, Override.DisabledBottomBorderRadius] })}
          {...childProps}
        />
      )
    })
  }

  const renderOptions = (): ReactNode => {
    return options?.map((option, index) => {
      const childId = `${id}-${index}`
      return <RadioButton key={childId} id={childId} label={option} value={option} />
    })
  }

  const parseChildren = (): ReactNode => {
    if (typeof children !== 'undefined') {
      return renderChildren()
    }

    return renderOptions()
  }

  useEffect(() => {
    hasValueProp && setSelectedValue(props.value ?? defaultValue)
  }, [hasValueProp, props.value, defaultValue])

  if (typeof children === 'undefined' && typeof options === 'undefined') {
    return null
  }

  const { value, ...pickedProps } = otherProps
  return (
    <RadioGroupContext.Provider
      value={{
        value: selectedValue,
        name,
        disabled,
        size,
        indent,
        kind,
        icon,
        reversed,
        labelWidth,
        labelPlacement,
        testNameSpace,
        onClick,
        onChange: handleChange,
      }}
    >
      <div
        role='radiogroup'
        className={styles.container}
        ref={ref}
        {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-group` })}
        {...pickedProps}
      >
        {parseChildren()}
      </div>
    </RadioGroupContext.Provider>
  )
})
